import { createFileRoute } from '@tanstack/react-router';
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Container,
  Divider,
  Group,
  NumberInput,
  SegmentedControl,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Switch,
  Tabs,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import {
  IconAdjustments,
  IconAlertCircle,
  IconBrandMeta,
  IconBuildingStore,
  IconCheck,
  IconCreditCard,
  IconKey,
  IconMail,
  IconRefresh,
  IconSend,
} from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';
import { PlatformReadinessPanel } from '../../components/config/PlatformReadinessPanel';
import { SecretField } from '../../components/config/SecretField';

// ── Field-def manifest ────────────────────────────────────────────────
//    Each tab is a list of sections; each section is a labelled `Card`
//    with a `SimpleGrid` of fields. `FieldDefPanel` below handles the
//    fetch / draft / save lifecycle for any tab built from this shape.

type FieldKind = 'text' | 'password' | 'number' | 'url' | 'email';

interface FieldDef {
  key: string;
  label: string;
  description?: string;
  placeholder?: string;
  kind: FieldKind;
  fullWidth?: boolean;
}

interface SectionDef {
  title: string;
  description?: string;
  fields: FieldDef[];
}

interface TabDef {
  sections: SectionDef[];
}

const GENERAL_TAB: TabDef = {
  sections: [
    {
      title: 'Identity & branding',
      description: 'Platform name and logo URLs shown in the navbar, login page, and emails.',
      fields: [
        {
          key: 'name',
          label: 'Platform name',
          description: 'Shown in browser tab, emails, and login screens.',
          placeholder: 'LeadSync',
          kind: 'text',
        },
        {
          key: 'logo_name',
          label: 'Wordmark logo URL',
          description: 'Public image URL — typically a horizontal logo for navbar use.',
          placeholder: 'https://your-cdn.com/wordmark.svg',
          kind: 'url',
          fullWidth: true,
        },
        {
          key: 'logo_dashboard',
          label: 'Dashboard logo URL',
          description: 'Logo shown on internal dashboards.',
          placeholder: 'https://your-cdn.com/logo.svg',
          kind: 'url',
          fullWidth: true,
        },
        {
          key: 'logo_favicon',
          label: 'Favicon URL',
          description: 'Browser tab icon — 32×32 PNG or SVG.',
          placeholder: 'https://your-cdn.com/favicon.png',
          kind: 'url',
          fullWidth: true,
        },
      ],
    },
    {
      title: 'Localization',
      description: 'Defaults applied to vendors that have not set their own.',
      fields: [
        {
          key: 'currency',
          label: 'Default currency code',
          description: 'ISO-4217 (USD, INR, EUR, GBP, …).',
          placeholder: 'USD',
          kind: 'text',
        },
      ],
    },
  ],
};

const EMAIL_TAB: TabDef = {
  sections: [
    {
      title: 'SMTP server',
      description:
        'Outbound email settings used for activation, password reset, and notifications.',
      fields: [
        {
          key: 'smtp_host',
          label: 'Host',
          placeholder: 'smtp.sendgrid.net',
          kind: 'text',
        },
        {
          key: 'smtp_port',
          label: 'Port',
          description: '587 (STARTTLS) or 465 (SMTPS).',
          placeholder: '587',
          kind: 'number',
        },
        {
          key: 'smtp_user',
          label: 'Username',
          kind: 'text',
        },
        {
          key: 'smtp_password',
          label: 'Password / API key',
          description: 'Stored encrypted server-side.',
          kind: 'password',
        },
      ],
    },
    {
      title: 'From identity',
      fields: [
        {
          key: 'smtp_from',
          label: 'From address',
          description: 'Verified sender (e.g. "LeadSync <no-reply@your-domain.com>").',
          placeholder: 'no-reply@your-domain.com',
          kind: 'email',
          fullWidth: true,
        },
      ],
    },
  ],
};

const SOCIAL_TAB: TabDef = {
  sections: [
    {
      title: 'Google Sign-In',
      description:
        'OAuth 2.0 Web server flow per developers.google.com/identity/protocols/oauth2/web-server. Both Client ID and Client Secret are required to exchange the auth code for an access token. Facebook Login is not configured here — it consumes META_APP_ID / META_APP_SECRET from the WhatsApp & Meta tab.',
      fields: [
        {
          key: 'google_client_id',
          label: 'Google OAuth Client ID',
          description:
            'From Google Cloud Console → APIs & Services → Credentials → OAuth 2.0 Client IDs.',
          placeholder: '…apps.googleusercontent.com',
          kind: 'text',
          fullWidth: true,
        },
        {
          key: 'google_client_secret',
          label: 'Google OAuth Client Secret',
          description:
            'From the same OAuth 2.0 Client ID row in Google Cloud Console. Stored encrypted server-side.',
          kind: 'password',
          fullWidth: true,
        },
      ],
    },
  ],
};

const WHATSAPP_META_TAB: TabDef = {
  sections: [
    // NB: the platform Meta App ID / Secret live in the "Vendor onboarding
    // controls" panel below (MetaControlsPanel), which writes them through
    // integrations.admin.saveMetaConfig — the App Secret is encrypted at rest
    // there. They are intentionally NOT duplicated here as plaintext config KV.
    {
      title: 'Webhook routing',
      description: 'Used by the WhatsApp app-level webhook subscription endpoint.',
      fields: [
        {
          key: 'webhook_verify_token',
          label: 'Verify token',
          description:
            'Shared secret echoed during the GET /webhook handshake. Rotate from the Webhook health page.',
          kind: 'password',
        },
        {
          key: 'whatsapp_app_callback_url',
          label: 'Public callback URL',
          description: 'HTTPS URL Meta posts inbound webhooks to. Must be publicly reachable.',
          placeholder: 'https://api.your-domain.com/api/whatsapp/webhook',
          kind: 'url',
          fullWidth: true,
        },
      ],
    },
  ],
};

function ConfigurationPage() {
  return (
    <Container size="lg" px={0}>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <Stack gap="md">
          <Title order={2}>System configuration</Title>
          <Tabs defaultValue="general" keepMounted={false}>
            <Tabs.List>
              <Tabs.Tab value="general" leftSection={<IconBuildingStore size={14} />}>
                General
              </Tabs.Tab>
              <Tabs.Tab value="email" leftSection={<IconMail size={14} />}>
                Email
              </Tabs.Tab>
              <Tabs.Tab value="whatsapp-meta" leftSection={<IconBrandMeta size={14} />}>
                WhatsApp &amp; Meta
              </Tabs.Tab>
              <Tabs.Tab value="social" leftSection={<IconKey size={14} />}>
                Social login
              </Tabs.Tab>
              <Tabs.Tab value="payment" leftSection={<IconCreditCard size={14} />}>
                Payments
              </Tabs.Tab>
              <Tabs.Tab value="misc" leftSection={<IconAdjustments size={14} />}>
                Platform
              </Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="general" pt="md">
              <FieldDefPanel tab={GENERAL_TAB} />
            </Tabs.Panel>
            <Tabs.Panel value="email" pt="md">
              <Stack gap="md">
                <FieldDefPanel tab={EMAIL_TAB} />
                <SmtpTestCard />
              </Stack>
            </Tabs.Panel>
            <Tabs.Panel value="whatsapp-meta" pt="md">
              <Stack gap="md">
                <FieldDefPanel tab={WHATSAPP_META_TAB} />
                {/* The verdict before the form: whether this configuration
                    currently lets anyone onboard, and how many vendors wait on it. */}
                <PlatformReadinessPanel />
                <MetaControlsPanel />
                <CatalogCredentialsPanel />
              </Stack>
            </Tabs.Panel>
            <Tabs.Panel value="social" pt="md">
              <FieldDefPanel tab={SOCIAL_TAB} />
            </Tabs.Panel>
            <Tabs.Panel value="payment" pt="md">
              <PaymentPanel />
            </Tabs.Panel>
            <Tabs.Panel value="misc" pt="md">
              <MiscPanel />
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </motion.div>
    </Container>
  );
}

function FieldRenderer({
  def,
  value,
  onChange,
}: {
  def: FieldDef;
  value: string;
  onChange: (v: string) => void;
}) {
  const common = {
    label: def.label,
    description: def.description,
    placeholder: def.placeholder,
    style: def.fullWidth ? { gridColumn: '1 / -1' } : undefined,
  };
  if (def.kind === 'password') {
    return <FieldRendererSecret def={def} value={value} onChange={onChange} common={common} />;
  }
  if (def.kind === 'number') {
    return (
      <NumberInput
        {...common}
        value={value === '' ? '' : Number(value)}
        onChange={(v) => onChange(v === '' ? '' : String(v))}
      />
    );
  }
  return (
    <TextInput
      {...common}
      type={def.kind === 'email' ? 'email' : def.kind === 'url' ? 'url' : 'text'}
      value={value}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  );
}

/**
 * Secret variant of a config field: adds the ✓ "Saved" badge + reveal eye.
 * Reveal reuses the existing SuperAdmin-gated `admin.config.get`, which returns
 * the decrypted value for a single key on demand (never on page load).
 */
function FieldRendererSecret({
  def,
  value,
  onChange,
  common,
}: {
  def: FieldDef;
  value: string;
  onChange: (v: string) => void;
  common: { label: string; description?: string; placeholder?: string; style?: CSSProperties };
}) {
  const utils = trpc.useUtils();
  return (
    <SecretField
      {...common}
      value={value}
      onChange={onChange}
      present={value !== ''}
      onReveal={() => utils.admin.config.get.fetch({ name: def.key })}
    />
  );
}

function FieldDefPanel({ tab }: { tab: TabDef }) {
  const all = trpc.admin.config.getAll.useQuery();
  const utils = trpc.useUtils();
  const setMany = trpc.admin.config.setMany.useMutation({
    onSuccess: () => {
      void utils.admin.config.getAll.invalidate();
      notifications.show({ message: 'Saved', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const [draft, setDraft] = useState<Record<string, string>>({});

  const allKeys = useMemo(() => tab.sections.flatMap((s) => s.fields.map((f) => f.key)), [tab]);

  const current = useMemo(() => {
    const out: Record<string, string> = {};
    for (const k of allKeys) out[k] = '';
    for (const row of all.data ?? []) {
      if (allKeys.includes(row.name)) out[row.name] = row.value ?? '';
    }
    return out;
  }, [allKeys, all.data]);

  useEffect(() => {
    setDraft(current);
  }, [current]);

  if (all.isPending) return <Skeleton height={300} />;

  return (
    <Stack gap="md">
      {tab.sections.map((section) => (
        <Card key={section.title} withBorder padding="lg">
          <Stack gap="sm">
            <div>
              <Title order={5}>{section.title}</Title>
              {section.description && (
                <Text size="xs" c="dimmed" mt={2}>
                  {section.description}
                </Text>
              )}
            </div>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm" verticalSpacing="sm">
              {section.fields.map((f) => (
                <FieldRenderer
                  key={f.key}
                  def={f}
                  value={draft[f.key] ?? ''}
                  onChange={(v) => setDraft((prev) => ({ ...prev, [f.key]: v }))}
                />
              ))}
            </SimpleGrid>
          </Stack>
        </Card>
      ))}
      <Group>
        <Button
          leftSection={<IconCheck size={14} />}
          loading={setMany.isPending}
          onClick={() => setMany.mutate({ settings: draft })}
        >
          Save changes
        </Button>
      </Group>
    </Stack>
  );
}

/**
 * SMTP test — verifies connection/auth against the SAVED Email-tab settings,
 * then sends a real test email. Stage-tagged errors distinguish "wrong
 * credentials" from "connected but can't send".
 */
function SmtpTestCard() {
  const [to, setTo] = useState('');
  const test = trpc.admin.config.testSmtp.useMutation({
    onSuccess: (r) => {
      if (r.ok) {
        notifications.show({
          title: 'Test email sent',
          message: `Delivered to ${to} — check the inbox (and spam folder).`,
          color: 'green',
        });
      } else {
        const title =
          r.stage === 'config'
            ? 'Not configured'
            : r.stage === 'verify'
              ? 'Connection / authentication failed'
              : 'Connected, but sending failed';
        notifications.show({ title, message: r.error, color: 'red', autoClose: 8000 });
      }
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  return (
    <Card withBorder padding="lg">
      <Stack gap="sm">
        <div>
          <Title order={5}>Test SMTP connection</Title>
          <Text size="xs" c="dimmed" mt={2}>
            Verifies the connection and credentials, then sends a real test email. Runs against the{' '}
            <b>saved</b> settings — click &ldquo;Save changes&rdquo; above first.
          </Text>
        </div>
        <Group align="flex-end">
          <TextInput
            label="Send test email to"
            type="email"
            placeholder="you@company.com"
            value={to}
            onChange={(e) => setTo(e.currentTarget.value)}
            style={{ flex: 1 }}
          />
          <Button
            leftSection={<IconSend size={14} />}
            loading={test.isPending}
            disabled={!to.includes('@')}
            onClick={() => test.mutate({ to })}
          >
            Send test
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

/**
 * Meta Controls — SuperAdmin sub-section under the WhatsApp & Meta tab.
 * Toggles which connection methods vendors can use platform-wide and stores
 * the Embedded Signup Config ID.
 */
function MetaControlsPanel() {
  const [esEnabled, setEsEnabled] = useState(true);
  const [manualEnabled, setManualEnabled] = useState(true);
  const [coexistenceEnabled, setCoexistenceEnabled] = useState(false);
  const [onboardingV2Enabled, setOnboardingV2Enabled] = useState(false);
  const [configId, setConfigId] = useState('');
  const [coexistenceFeatureType, setCoexistenceFeatureType] = useState('');
  const [facebookConfigId, setFacebookConfigId] = useState('');
  const [instagramConfigId, setInstagramConfigId] = useState('');
  const [humanAgentEnabled, setHumanAgentEnabled] = useState(false);
  const [appId, setAppId] = useState('');
  // Secret is write-only: never returned by the API. Empty input = keep existing.
  const [appSecret, setAppSecret] = useState('');
  const [igAppId, setIgAppId] = useState('');
  const [igAppSecret, setIgAppSecret] = useState('');

  const utils = trpc.useUtils();
  const config = trpc.integrations.admin.getMetaConfig.useQuery();
  const save = trpc.integrations.admin.saveMetaConfig.useMutation({
    onSuccess: () => {
      void config.refetch();
      setAppSecret('');
      setIgAppSecret('');
      notifications.show({ message: 'Meta configuration saved', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  useEffect(() => {
    if (!config.data) return;
    setEsEnabled(config.data.embeddedSignupEnabled);
    setManualEnabled(config.data.manualEnabled);
    setCoexistenceEnabled(config.data.coexistenceEnabled ?? false);
    setOnboardingV2Enabled(config.data.onboardingV2Enabled ?? false);
    setConfigId(config.data.configId ?? '');
    setCoexistenceFeatureType(config.data.coexistenceFeatureType ?? '');
    setFacebookConfigId(config.data.facebookConfigId ?? '');
    setInstagramConfigId(config.data.instagramConfigId ?? '');
    setHumanAgentEnabled(config.data.humanAgentEnabled ?? false);
    setAppId(config.data.appId ?? '');
    setIgAppId(config.data.instagramAppId ?? '');
  }, [config.data]);

  if (config.isPending) return <Skeleton height={240} />;

  return (
    <Card withBorder padding="lg">
      <Stack gap="sm">
        <div>
          <Title order={5}>Vendor onboarding controls</Title>
          <Text size="xs" c="dimmed" mt={2}>
            Choose which connection methods vendors can use to attach a WhatsApp number. Disabling a
            method hides it on the vendor Meta Configuration page.
          </Text>
        </div>

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm" verticalSpacing="sm">
          <Switch
            label="Embedded Login (recommended)"
            description="FB.login() popup flow. Requires the Config ID below."
            checked={esEnabled}
            onChange={(e) => setEsEnabled(e.currentTarget.checked)}
          />
          <Switch
            label="Manual credentials"
            description="Vendor pastes Permanent Access Token + WABA ID + Phone Number ID."
            checked={manualEnabled}
            onChange={(e) => setManualEnabled(e.currentTarget.checked)}
          />
          <Switch
            label="Coexistence (existing number)"
            description="Let vendors link an existing WhatsApp Business app number via the JS-SDK popup. Requires the Config ID + featureType below; rolled out per-region by Meta."
            checked={coexistenceEnabled}
            onChange={(e) => setCoexistenceEnabled(e.currentTarget.checked)}
          />
          <Switch
            label="Guided onboarding (new)"
            description="Send new vendors through the step-by-step setup at /vendor/setup and the unified Connections page, instead of the Meta setup tabs. Turning it off restores today's journey immediately — nothing is migrated either way."
            checked={onboardingV2Enabled}
            onChange={(e) => setOnboardingV2Enabled(e.currentTarget.checked)}
          />
          <TextInput
            label="Embedded Signup Config ID"
            description="From Meta Business Suite → Embedded Signup → your configuration."
            placeholder="123456789"
            value={configId}
            onChange={(e) => setConfigId(e.currentTarget.value)}
            style={{ gridColumn: '1 / -1' }}
          />
          <TextInput
            label="Coexistence featureType"
            description="Meta extras.featureType value that triggers coexistence onboarding. Confirm against Meta's 'Onboarding Business app users' docs before enabling."
            placeholder="whatsapp_business_app_onboarding"
            value={coexistenceFeatureType}
            onChange={(e) => setCoexistenceFeatureType(e.currentTarget.value)}
            style={{ gridColumn: '1 / -1' }}
          />
          <Switch
            label="Human Agent replies (Messenger + Instagram)"
            description="Only turn this on once Meta has approved the Human Agent feature in App Review. It extends the reply window from 24 hours to 7 days. Without the approval Meta rejects those replies outright, so we block them locally instead."
            checked={humanAgentEnabled}
            onChange={(e) => setHumanAgentEnabled(e.currentTarget.checked)}
            style={{ gridColumn: '1 / -1' }}
          />
          <TextInput
            label="Messenger Config ID"
            description="Facebook Login for Business configuration for connecting Pages. Leave blank to use the redirect sign-in instead."
            placeholder="123456789"
            value={facebookConfigId}
            onChange={(e) => setFacebookConfigId(e.currentTarget.value)}
          />
          <TextInput
            label="Instagram Config ID"
            description="Facebook Login for Business configuration for connecting Instagram accounts linked to a Page. Leave blank to use the redirect sign-in instead."
            placeholder="123456789"
            value={instagramConfigId}
            onChange={(e) => setInstagramConfigId(e.currentTarget.value)}
          />
        </SimpleGrid>

        <Divider my="xs" />
        <div>
          <Title order={5}>Platform Meta App credentials</Title>
          <Text size="xs" c="dimmed" mt={2}>
            The Meta App ID / Secret used for Embedded Signup and OAuth. Read from here (or a
            vendor&apos;s own BYOC credentials) — never from a server <code>.env</code> file. A
            vendor that sets their own app overrides these.
          </Text>
        </div>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm" verticalSpacing="sm">
          <TextInput
            label="Meta App ID"
            description="From Meta App Dashboard → Settings → Basic."
            placeholder="1234567890123456"
            value={appId}
            onChange={(e) => setAppId(e.currentTarget.value)}
          />
          <SecretField
            label="Meta App Secret"
            description={
              config.data?.hasAppSecret
                ? 'A secret is saved (encrypted). Leave blank to keep it; click the eye to reveal.'
                : 'Encrypted at rest. Required for Embedded Signup.'
            }
            placeholder="App secret"
            present={!!config.data?.hasAppSecret}
            value={appSecret}
            onChange={setAppSecret}
            onReveal={() => utils.admin.config.get.fetch({ name: 'META_APP_SECRET' })}
          />
        </SimpleGrid>

        <Divider my="xs" />
        <div>
          <Title order={5}>Platform Instagram App credentials</Title>
          <Text size="xs" c="dimmed" mt={2}>
            A <b>separate</b> app from the Meta App above — from App Dashboard → Instagram → API
            setup with Instagram login → Business login settings. Required so standalone Instagram
            (Business Login) accounts can be verified: Meta signs those webhooks with the Instagram
            App Secret, not the Facebook one. A vendor can override with their own via BYOC.
          </Text>
        </div>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm" verticalSpacing="sm">
          <TextInput
            label="Instagram App ID"
            description="App Dashboard → Instagram → API setup with Instagram login."
            placeholder="1234567890123456"
            value={igAppId}
            onChange={(e) => setIgAppId(e.currentTarget.value)}
          />
          <SecretField
            label="Instagram App Secret"
            description={
              config.data?.hasInstagramAppSecret
                ? 'A secret is saved (encrypted). Leave blank to keep it; click the eye to reveal.'
                : 'Encrypted at rest. Required for standalone Instagram (Business Login).'
            }
            placeholder="Instagram app secret"
            present={!!config.data?.hasInstagramAppSecret}
            value={igAppSecret}
            onChange={setIgAppSecret}
            onReveal={() => utils.admin.config.get.fetch({ name: 'INSTAGRAM_APP_SECRET' })}
          />
        </SimpleGrid>

        <Group>
          <Button
            loading={save.isPending}
            leftSection={<IconCheck size={14} />}
            onClick={() =>
              save.mutate({
                embeddedSignupEnabled: esEnabled,
                manualEnabled,
                coexistenceEnabled,
                onboardingV2Enabled,
                configId,
                coexistenceFeatureType: coexistenceFeatureType || undefined,
                facebookConfigId,
                instagramConfigId,
                humanAgentEnabled,
                appId,
                // Only send the secret when the admin actually typed one;
                // blank means "keep the existing encrypted value".
                appSecret: appSecret.trim() ? appSecret.trim() : undefined,
                instagramAppId: igAppId,
                instagramAppSecret: igAppSecret.trim() ? igAppSecret.trim() : undefined,
              })
            }
          >
            Save onboarding controls
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

/**
 * Meta Product Catalog credentials (Model A auto-scope toggle + Model B
 * platform System-User token override). Sits under the WhatsApp & Meta tab.
 */
function CatalogCredentialsPanel() {
  const utils = trpc.useUtils();
  const creds = trpc.admin.catalogCreds.get.useQuery();
  const [businessId, setBusinessId] = useState('');
  const [token, setToken] = useState('');
  const [autoScope, setAutoScope] = useState(false);

  useEffect(() => {
    if (!creds.data) return;
    setBusinessId(creds.data.businessId ?? '');
    setAutoScope(creds.data.autoScope);
  }, [creds.data]);

  const save = trpc.admin.catalogCreds.set.useMutation({
    onSuccess: () => {
      void utils.admin.catalogCreds.get.invalidate();
      setToken('');
      notifications.show({ message: 'Catalog credentials saved', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const test = trpc.admin.catalogCreds.test.useMutation({
    onSuccess: (r) =>
      notifications.show({
        title: 'Catalog token OK',
        message: `Reached ${r.count} catalog(s).`,
        color: 'green',
      }),
    onError: (err) => notifications.show({ message: err.message, color: 'red', autoClose: 8000 }),
  });
  const clear = trpc.admin.catalogCreds.clear.useMutation({
    onSuccess: () => {
      void utils.admin.catalogCreds.get.invalidate();
      notifications.show({ message: 'Cleared', color: 'orange' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  if (creds.isPending) return <Skeleton height={220} />;

  return (
    <Card withBorder padding="lg">
      <Stack gap="sm">
        <div>
          <Title order={5}>Meta Product Catalog</Title>
          <Text size="xs" c="dimmed" mt={2}>
            Catalog calls normally reuse each vendor&apos;s WhatsApp connection — no callback URL or
            webhook is required. Options below cover the two token models for a Tech Provider.
          </Text>
        </div>

        <Switch
          label="Request catalog_management on WhatsApp connect (auto)"
          description="Turn on only after your Meta app has catalog_management (Advanced Access); otherwise new connections fail with 'Invalid Scopes'. Embedded Signup scopes are set on your ES config in the Meta dashboard."
          checked={autoScope}
          onChange={(e) => {
            setAutoScope(e.currentTarget.checked);
            save.mutate({ autoScope: e.currentTarget.checked });
          }}
        />

        <Divider my="xs" label="Platform token override (optional)" labelPosition="left" />
        <Text size="xs" c="dimmed">
          A platform System User token used for catalog calls when a vendor&apos;s own token
          can&apos;t reach the Catalog API — for testing, BYOC vendors, or platform-owned catalogs.
        </Text>
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm" verticalSpacing="sm">
          <TextInput
            label="Catalog business id"
            description="Business that owns the catalogs. Blank = use each vendor's own business."
            placeholder="1234567890"
            value={businessId}
            onChange={(e) => setBusinessId(e.currentTarget.value)}
          />
          <SecretField
            label="System User token"
            description={
              creds.data?.hasToken
                ? 'A token is saved (encrypted). Leave blank to keep it; click the eye to reveal.'
                : 'Encrypted at rest.'
            }
            placeholder="EAAG…"
            present={!!creds.data?.hasToken}
            value={token}
            onChange={setToken}
            onReveal={() =>
              utils.admin.config.get.fetch({ name: 'meta_catalog_system_user_token' })
            }
          />
        </SimpleGrid>

        <Group>
          <Button
            leftSection={<IconCheck size={14} />}
            loading={save.isPending}
            onClick={() =>
              save.mutate({ token: token.trim() || undefined, businessId: businessId.trim() })
            }
          >
            Save
          </Button>
          <Button variant="default" loading={test.isPending} onClick={() => test.mutate()}>
            Test connection
          </Button>
          {creds.data?.hasToken && (
            <Button
              variant="subtle"
              color="red"
              loading={clear.isPending}
              onClick={() => clear.mutate()}
            >
              Clear
            </Button>
          )}
        </Group>
      </Stack>
    </Card>
  );
}

// ── Misc panel — registration controls, ToS/Privacy URLs, bot timing,
//    AI defaults, theme, CSV import limit, cache clear. Mirror of the
//    .NET ConfigMisc.razor page, collapsed into the unified config tabs.
const MISC_KEYS = [
  'registration_enabled',
  'registration_activation_required',
  'registration_block_disposable_email',
  'welcome_email_enabled',
  'terms_of_service_url',
  'vendor_terms_of_service_url',
  'privacy_policy_url',
  'bot_timing_window_start_hour',
  'bot_timing_window_end_hour',
  'bot_timing_timezone',
  'ai_bot_default_enabled',
  'default_theme',
  'csv_import_max_rows',
] as const;

const TIMEZONES = [
  'UTC',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'America/Los_Angeles',
  'Australia/Sydney',
];

function MiscPanel() {
  const all = trpc.admin.config.getAll.useQuery();
  const utils = trpc.useUtils();
  const setMany = trpc.admin.config.setMany.useMutation({
    onSuccess: () => {
      void utils.admin.config.getAll.invalidate();
      notifications.show({ message: 'Saved', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const cacheClear = trpc.admin.cache.clear.useMutation({
    onSuccess: ({ deleted }) =>
      notifications.show({
        message: `Cleared ${deleted} cache key${deleted === 1 ? '' : 's'}`,
        color: 'green',
      }),
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const storageTest = trpc.admin.systemTest.storage.useMutation({
    onSuccess: (r) => {
      if (r.ok) {
        notifications.show({
          title: `Storage OK (${r.driver})`,
          message: r.detail ?? 'Reachable.',
          color: 'green',
        });
      } else {
        notifications.show({
          title: `Storage test failed (${r.driver})`,
          message: r.error,
          color: 'red',
          autoClose: 8000,
        });
      }
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const [draft, setDraft] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!all.data) return;
    const seed: Record<string, string> = {};
    for (const k of MISC_KEYS) {
      const row = all.data.find((c) => c.name === k);
      seed[k] = row?.value ?? '';
    }
    setDraft(seed);
  }, [all.data]);

  if (all.isPending) return <Skeleton height={400} />;

  const bool = (k: string): boolean => draft[k] === '1' || draft[k]?.toLowerCase() === 'true';
  const setBool = (k: string, v: boolean) => setDraft((prev) => ({ ...prev, [k]: v ? '1' : '0' }));
  const setStr = (k: string, v: string) => setDraft((prev) => ({ ...prev, [k]: v }));

  const save = () => {
    const settings: Record<string, string | null> = {};
    for (const name of MISC_KEYS) settings[name] = draft[name] ?? '';
    setMany.mutate({ settings });
  };

  return (
    <Stack gap="md">
      <Card withBorder padding="lg">
        <Stack gap="sm">
          <div>
            <Title order={5}>Registration</Title>
            <Text size="xs" c="dimmed" mt={2}>
              Controls who can sign up and how new accounts are activated.
            </Text>
          </div>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm" verticalSpacing="sm">
            <Switch
              label="Enable user registration"
              description="When off, the public sign-up form is hidden — vendors must be invited."
              checked={bool('registration_enabled')}
              onChange={(e) => setBool('registration_enabled', e.currentTarget.checked)}
            />
            <Switch
              label="Require email activation"
              description="New accounts must click an activation link before logging in."
              checked={bool('registration_activation_required')}
              onChange={(e) => setBool('registration_activation_required', e.currentTarget.checked)}
            />
            <Switch
              label="Block disposable email domains"
              description="Reject sign-ups using throwaway providers (mailinator, yopmail, etc.)."
              checked={bool('registration_block_disposable_email')}
              onChange={(e) =>
                setBool('registration_block_disposable_email', e.currentTarget.checked)
              }
            />
            <Switch
              label="Send welcome email"
              description="Send a welcome email when a new account is created."
              checked={bool('welcome_email_enabled')}
              onChange={(e) => setBool('welcome_email_enabled', e.currentTarget.checked)}
            />
          </SimpleGrid>
        </Stack>
      </Card>

      <Card withBorder padding="lg">
        <Stack gap="sm">
          <div>
            <Title order={5}>Legal URLs</Title>
            <Text size="xs" c="dimmed" mt={2}>
              Required for Meta App Review. All URLs must be publicly crawlable (no auth gate).
            </Text>
          </div>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm" verticalSpacing="sm">
            <TextInput
              label="Terms of service (end users)"
              placeholder="https://your-site.com/terms"
              value={draft['terms_of_service_url'] ?? ''}
              onChange={(e) => setStr('terms_of_service_url', e.currentTarget.value)}
            />
            <TextInput
              label="Terms of service (vendors / business)"
              placeholder="https://your-site.com/vendor-terms"
              value={draft['vendor_terms_of_service_url'] ?? ''}
              onChange={(e) => setStr('vendor_terms_of_service_url', e.currentTarget.value)}
            />
            <TextInput
              label="Privacy policy"
              placeholder="https://your-site.com/privacy"
              value={draft['privacy_policy_url'] ?? ''}
              onChange={(e) => setStr('privacy_policy_url', e.currentTarget.value)}
              style={{ gridColumn: '1 / -1' }}
            />
          </SimpleGrid>
        </Stack>
      </Card>

      <Card withBorder padding="lg">
        <Stack gap="sm">
          <div>
            <Title order={5}>Default bot timing window</Title>
            <Text size="xs" c="dimmed" mt={2}>
              Vendor-level overrides on Settings → AI &amp; Bot Timing take precedence. This is the
              platform-wide default applied to vendors that haven&apos;t set their own window.
            </Text>
          </div>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="sm" verticalSpacing="sm">
            <NumberInput
              label="Window start hour (0–23)"
              min={0}
              max={23}
              value={Number(draft['bot_timing_window_start_hour'] ?? 9)}
              onChange={(v) => setStr('bot_timing_window_start_hour', String(v ?? 9))}
            />
            <NumberInput
              label="Window end hour (0–23)"
              min={0}
              max={23}
              value={Number(draft['bot_timing_window_end_hour'] ?? 21)}
              onChange={(v) => setStr('bot_timing_window_end_hour', String(v ?? 21))}
            />
            <Select
              label="Timezone"
              data={TIMEZONES}
              value={draft['bot_timing_timezone'] || 'UTC'}
              onChange={(v) => setStr('bot_timing_timezone', v ?? 'UTC')}
              searchable
            />
          </SimpleGrid>
        </Stack>
      </Card>

      <Card withBorder padding="lg">
        <Stack gap="sm">
          <div>
            <Title order={5}>Defaults</Title>
            <Text size="xs" c="dimmed" mt={2}>
              Platform-wide defaults applied to new contacts and new vendor accounts.
            </Text>
          </div>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm" verticalSpacing="sm">
            <Switch
              label="Enable AI bot by default for new contacts"
              description="When on, the AI bot toggle on a freshly-created contact starts ON."
              checked={bool('ai_bot_default_enabled')}
              onChange={(e) => setBool('ai_bot_default_enabled', e.currentTarget.checked)}
            />
            <NumberInput
              label="CSV import row limit"
              description="Maximum rows allowed in a single contact import. Larger files are rejected at upload."
              min={100}
              max={500_000}
              step={1000}
              value={Number(draft['csv_import_max_rows'] ?? 50_000)}
              onChange={(v) => setStr('csv_import_max_rows', String(v ?? 50_000))}
            />
            <Stack gap={4} style={{ gridColumn: '1 / -1' }}>
              <Text size="sm" fw={500}>
                Default theme
              </Text>
              <SegmentedControl
                data={[
                  { value: 'system', label: 'System' },
                  { value: 'light', label: 'Light' },
                  { value: 'dark', label: 'Dark' },
                ]}
                value={draft['default_theme'] || 'system'}
                onChange={(v) => setStr('default_theme', v)}
              />
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>

      <Group>
        <Button leftSection={<IconCheck size={14} />} loading={setMany.isPending} onClick={save}>
          Save platform settings
        </Button>
      </Group>

      <Divider my="sm" />

      <Card withBorder padding="lg">
        <Stack gap="sm">
          <div>
            <Title order={5}>Media storage</Title>
            <Text size="xs" c="dimmed" mt={2}>
              Read-only reachability check of the configured storage driver (local disk or S3/MinIO)
              — where WhatsApp media uploads land.
            </Text>
          </div>
          <Group>
            <Button
              variant="default"
              leftSection={<IconCheck size={14} />}
              loading={storageTest.isPending}
              onClick={() => storageTest.mutate()}
            >
              Test storage
            </Button>
          </Group>
        </Stack>
      </Card>

      <Card withBorder padding="lg">
        <Stack gap="sm">
          <div>
            <Title order={5}>Server cache</Title>
            <Text size="xs" c="dimmed" mt={2}>
              Flushes the cache layer after rotating credentials or invalidating stale data.
            </Text>
          </div>
          <Alert color="orange" variant="light" icon={<IconAlertCircle size={16} />}>
            Flushes every Redis key under the <code>leadsync:*</code> namespace. Use this after
            rotating Meta App credentials or whenever cached templates / phone numbers / page
            insights look stale. Sessions, BullMQ queues, and OAuth state are NOT touched.
          </Alert>
          <Group>
            <Button
              variant="default"
              color="orange"
              leftSection={<IconRefresh size={14} />}
              loading={cacheClear.isPending}
              onClick={() => cacheClear.mutate()}
            >
              Clear server cache
            </Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}

// ── Payment panel — multi-gateway. Stripe is the only gateway that
//    actually processes charges today; the others (PayPal, Razorpay,
//    Paystack, UPI, Bank transfer) are stored for future activation
//    so onboarding paperwork doesn't get lost. Mirrors the .NET
//    ConfigPayment.razor layout.
const PAYMENT_KEYS = [
  // Stripe
  'enable_stripe',
  'stripe_public_key',
  'stripe_secret_key',
  'stripe_webhook_secret',
  // PayPal
  'enable_paypal',
  'paypal_sandbox',
  'paypal_client_id',
  'paypal_client_secret',
  'paypal_webhook_id',
  // Razorpay
  'enable_razorpay',
  'razorpay_key_id',
  'razorpay_key_secret',
  'razorpay_webhook_secret',
  // Paystack
  'enable_paystack',
  'paystack_public_key',
  'paystack_secret_key',
  // UPI
  'enable_upi',
  'upi_vpa',
  'upi_merchant_name',
  'upi_qr_image_url',
  // Bank Transfer
  'enable_bank_transfer',
  'bank_name',
  'bank_account_name',
  'bank_account_number',
  'bank_ifsc_swift',
  'bank_transfer_instructions',
] as const;

function PaymentPanel() {
  const all = trpc.admin.config.getAll.useQuery();
  const utils = trpc.useUtils();
  const setMany = trpc.admin.config.setMany.useMutation({
    onSuccess: () => {
      void utils.admin.config.getAll.invalidate();
      notifications.show({ message: 'Payment configuration saved', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const stripeTest = trpc.admin.systemTest.stripe.useMutation({
    onSuccess: (r) => {
      if (r.ok) {
        notifications.show({
          title: 'Stripe connected',
          message: `Secret key is valid (${r.mode} mode).`,
          color: 'green',
        });
      } else {
        notifications.show({
          title: 'Stripe test failed',
          message: r.error,
          color: 'red',
          autoClose: 8000,
        });
      }
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const [draft, setDraft] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!all.data) return;
    const seed: Record<string, string> = {};
    for (const k of PAYMENT_KEYS) {
      const row = all.data.find((c) => c.name === k);
      seed[k] = row?.value ?? '';
    }
    setDraft(seed);
  }, [all.data]);

  if (all.isPending) return <Skeleton height={400} />;

  const bool = (k: string): boolean => draft[k] === '1' || draft[k]?.toLowerCase() === 'true';
  const setBool = (k: string, v: boolean) => setDraft((prev) => ({ ...prev, [k]: v ? '1' : '0' }));
  const setStr = (k: string, v: string) => setDraft((prev) => ({ ...prev, [k]: v }));

  const save = () => {
    const settings: Record<string, string | null> = {};
    for (const name of PAYMENT_KEYS) settings[name] = draft[name] ?? '';
    setMany.mutate({ settings });
  };

  const enabledBadge = (k: string) =>
    bool(k) ? (
      <Badge color="green" variant="light" size="sm">
        enabled
      </Badge>
    ) : (
      <Badge color="gray" variant="light" size="sm">
        off
      </Badge>
    );

  return (
    <Stack gap="md">
      <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />}>
        <Text size="sm">
          <b>Stripe</b> is the only gateway that processes charges today. The other gateways are
          stored here for future activation so onboarding paperwork doesn&apos;t get lost.
        </Text>
      </Alert>

      <Accordion variant="separated" multiple defaultValue={['stripe']}>
        <Accordion.Item value="stripe">
          <Accordion.Control>
            <Group justify="space-between" wrap="nowrap">
              <Text fw={600}>Stripe</Text>
              {enabledBadge('enable_stripe')}
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="sm">
              <Switch
                label="Enable Stripe"
                description="Allow vendors to subscribe via Stripe Checkout."
                checked={bool('enable_stripe')}
                onChange={(e) => setBool('enable_stripe', e.currentTarget.checked)}
              />
              {bool('enable_stripe') && (
                <>
                  <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm" verticalSpacing="sm">
                    <TextInput
                      label="Publishable key"
                      placeholder="pk_live_… or pk_test_…"
                      value={draft['stripe_public_key'] ?? ''}
                      onChange={(e) => setStr('stripe_public_key', e.currentTarget.value)}
                    />
                    <SecretField
                      label="Secret key"
                      placeholder="sk_live_… or sk_test_…"
                      present={(draft['stripe_secret_key'] ?? '') !== ''}
                      value={draft['stripe_secret_key'] ?? ''}
                      onChange={(v) => setStr('stripe_secret_key', v)}
                      onReveal={() => utils.admin.config.get.fetch({ name: 'stripe_secret_key' })}
                    />
                    <SecretField
                      label="Webhook signing secret"
                      description="From Stripe Dashboard → Developers → Webhooks. Verifies checkout.session.completed events."
                      placeholder="whsec_…"
                      present={(draft['stripe_webhook_secret'] ?? '') !== ''}
                      value={draft['stripe_webhook_secret'] ?? ''}
                      onChange={(v) => setStr('stripe_webhook_secret', v)}
                      onReveal={() =>
                        utils.admin.config.get.fetch({ name: 'stripe_webhook_secret' })
                      }
                      style={{ gridColumn: '1 / -1' }}
                    />
                  </SimpleGrid>
                  <Group>
                    <Button
                      variant="default"
                      size="xs"
                      loading={stripeTest.isPending}
                      onClick={() => stripeTest.mutate()}
                    >
                      Test connection
                    </Button>
                    <Text size="xs" c="dimmed">
                      Read-only key check against the <b>saved</b> secret key — save first.
                    </Text>
                  </Group>
                </>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="paypal">
          <Accordion.Control>
            <Group justify="space-between" wrap="nowrap">
              <Text fw={600}>PayPal</Text>
              <Group gap="xs">
                <Badge color="orange" variant="light" size="sm">
                  not active
                </Badge>
                {enabledBadge('enable_paypal')}
              </Group>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="sm">
              <Switch
                label="Enable PayPal"
                description="Stored for future activation — does not process charges yet."
                checked={bool('enable_paypal')}
                onChange={(e) => setBool('enable_paypal', e.currentTarget.checked)}
              />
              {bool('enable_paypal') && (
                <Stack gap="sm">
                  <Switch
                    label="Sandbox mode"
                    description="Use PayPal sandbox endpoints instead of live."
                    checked={bool('paypal_sandbox')}
                    onChange={(e) => setBool('paypal_sandbox', e.currentTarget.checked)}
                  />
                  <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm" verticalSpacing="sm">
                    <TextInput
                      label="Client ID"
                      description="From developer.paypal.com → My Apps & Credentials → REST API app."
                      value={draft['paypal_client_id'] ?? ''}
                      onChange={(e) => setStr('paypal_client_id', e.currentTarget.value)}
                    />
                    <SecretField
                      label="Client secret"
                      present={(draft['paypal_client_secret'] ?? '') !== ''}
                      value={draft['paypal_client_secret'] ?? ''}
                      onChange={(v) => setStr('paypal_client_secret', v)}
                      onReveal={() =>
                        utils.admin.config.get.fetch({ name: 'paypal_client_secret' })
                      }
                    />
                    <TextInput
                      label="Webhook ID"
                      description="From REST app → Webhooks. Used to verify incoming IPN signatures."
                      placeholder="3X1234567A012345B"
                      value={draft['paypal_webhook_id'] ?? ''}
                      onChange={(e) => setStr('paypal_webhook_id', e.currentTarget.value)}
                      style={{ gridColumn: '1 / -1' }}
                    />
                  </SimpleGrid>
                </Stack>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="razorpay">
          <Accordion.Control>
            <Group justify="space-between" wrap="nowrap">
              <Text fw={600}>Razorpay</Text>
              <Group gap="xs">
                <Badge color="orange" variant="light" size="sm">
                  not active
                </Badge>
                {enabledBadge('enable_razorpay')}
              </Group>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="sm">
              <Switch
                label="Enable Razorpay"
                description="Stored for future activation — does not process charges yet."
                checked={bool('enable_razorpay')}
                onChange={(e) => setBool('enable_razorpay', e.currentTarget.checked)}
              />
              {bool('enable_razorpay') && (
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm" verticalSpacing="sm">
                  <TextInput
                    label="Key ID"
                    description="From Razorpay Dashboard → Settings → API Keys."
                    placeholder="rzp_live_… or rzp_test_…"
                    value={draft['razorpay_key_id'] ?? ''}
                    onChange={(e) => setStr('razorpay_key_id', e.currentTarget.value)}
                  />
                  <SecretField
                    label="Key secret"
                    present={(draft['razorpay_key_secret'] ?? '') !== ''}
                    value={draft['razorpay_key_secret'] ?? ''}
                    onChange={(v) => setStr('razorpay_key_secret', v)}
                    onReveal={() => utils.admin.config.get.fetch({ name: 'razorpay_key_secret' })}
                  />
                  <SecretField
                    label="Webhook secret"
                    description="From Razorpay Dashboard → Settings → Webhooks. Verifies payment.captured events."
                    present={(draft['razorpay_webhook_secret'] ?? '') !== ''}
                    value={draft['razorpay_webhook_secret'] ?? ''}
                    onChange={(v) => setStr('razorpay_webhook_secret', v)}
                    onReveal={() =>
                      utils.admin.config.get.fetch({ name: 'razorpay_webhook_secret' })
                    }
                    style={{ gridColumn: '1 / -1' }}
                  />
                </SimpleGrid>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="paystack">
          <Accordion.Control>
            <Group justify="space-between" wrap="nowrap">
              <Text fw={600}>Paystack</Text>
              <Group gap="xs">
                <Badge color="orange" variant="light" size="sm">
                  not active
                </Badge>
                {enabledBadge('enable_paystack')}
              </Group>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="sm">
              <Switch
                label="Enable Paystack"
                description="Stored for future activation — does not process charges yet."
                checked={bool('enable_paystack')}
                onChange={(e) => setBool('enable_paystack', e.currentTarget.checked)}
              />
              {bool('enable_paystack') && (
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm" verticalSpacing="sm">
                  <TextInput
                    label="Public key"
                    description="From Paystack Dashboard → Settings → API Keys & Webhooks."
                    placeholder="pk_live_… or pk_test_…"
                    value={draft['paystack_public_key'] ?? ''}
                    onChange={(e) => setStr('paystack_public_key', e.currentTarget.value)}
                  />
                  <SecretField
                    label="Secret key"
                    description="Also used as the HMAC SHA-512 key for webhook signature verification."
                    placeholder="sk_live_… or sk_test_…"
                    present={(draft['paystack_secret_key'] ?? '') !== ''}
                    value={draft['paystack_secret_key'] ?? ''}
                    onChange={(v) => setStr('paystack_secret_key', v)}
                    onReveal={() => utils.admin.config.get.fetch({ name: 'paystack_secret_key' })}
                  />
                </SimpleGrid>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="upi">
          <Accordion.Control>
            <Group justify="space-between" wrap="nowrap">
              <Text fw={600}>UPI (manual)</Text>
              <Group gap="xs">
                <Badge color="orange" variant="light" size="sm">
                  not active
                </Badge>
                {enabledBadge('enable_upi')}
              </Group>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="sm">
              <Switch
                label="Enable UPI"
                description="Show UPI payment instructions on manual checkout."
                checked={bool('enable_upi')}
                onChange={(e) => setBool('enable_upi', e.currentTarget.checked)}
              />
              {bool('enable_upi') && (
                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm" verticalSpacing="sm">
                  <TextInput
                    label="UPI VPA"
                    placeholder="yourbusiness@okicici"
                    value={draft['upi_vpa'] ?? ''}
                    onChange={(e) => setStr('upi_vpa', e.currentTarget.value)}
                  />
                  <TextInput
                    label="Merchant name (as displayed in UPI app)"
                    value={draft['upi_merchant_name'] ?? ''}
                    onChange={(e) => setStr('upi_merchant_name', e.currentTarget.value)}
                  />
                  <TextInput
                    label="QR code image URL"
                    description="Public image URL of your UPI QR. Shown to customers on the manual checkout page."
                    placeholder="https://your-cdn.com/upi-qr.png"
                    value={draft['upi_qr_image_url'] ?? ''}
                    onChange={(e) => setStr('upi_qr_image_url', e.currentTarget.value)}
                    style={{ gridColumn: '1 / -1' }}
                  />
                </SimpleGrid>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        <Accordion.Item value="bank">
          <Accordion.Control>
            <Group justify="space-between" wrap="nowrap">
              <Text fw={600}>Bank transfer (manual)</Text>
              <Group gap="xs">
                <Badge color="orange" variant="light" size="sm">
                  not active
                </Badge>
                {enabledBadge('enable_bank_transfer')}
              </Group>
            </Group>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="sm">
              <Switch
                label="Enable bank transfer"
                description="Show bank account details on the manual checkout page so customers can wire payments directly."
                checked={bool('enable_bank_transfer')}
                onChange={(e) => setBool('enable_bank_transfer', e.currentTarget.checked)}
              />
              {bool('enable_bank_transfer') && (
                <Stack gap="sm">
                  <SimpleGrid cols={{ base: 1, md: 2 }} spacing="sm" verticalSpacing="sm">
                    <TextInput
                      label="Bank name"
                      value={draft['bank_name'] ?? ''}
                      onChange={(e) => setStr('bank_name', e.currentTarget.value)}
                    />
                    <TextInput
                      label="Account holder name"
                      value={draft['bank_account_name'] ?? ''}
                      onChange={(e) => setStr('bank_account_name', e.currentTarget.value)}
                    />
                    <TextInput
                      label="Account number"
                      value={draft['bank_account_number'] ?? ''}
                      onChange={(e) => setStr('bank_account_number', e.currentTarget.value)}
                    />
                    <TextInput
                      label="IFSC / SWIFT / routing code"
                      value={draft['bank_ifsc_swift'] ?? ''}
                      onChange={(e) => setStr('bank_ifsc_swift', e.currentTarget.value)}
                    />
                  </SimpleGrid>
                  <Textarea
                    label="Transfer instructions"
                    description="Free-form notes shown to customers (memo / reference format, hours, support email)."
                    autosize
                    minRows={3}
                    value={draft['bank_transfer_instructions'] ?? ''}
                    onChange={(e) => setStr('bank_transfer_instructions', e.currentTarget.value)}
                  />
                </Stack>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>

      <Group>
        <Button leftSection={<IconCheck size={14} />} loading={setMany.isPending} onClick={save}>
          Save payment configuration
        </Button>
      </Group>
    </Stack>
  );
}

export const Route = createFileRoute('/central/configuration')({ component: ConfigurationPage });
